// ゲーム内アンケート。設問はデータ（運営APIで D1 に入れる）なので、型・検証・集計をここに置き、画面と worker で共有する。
// 回答は選択肢の番号で持つ。回答が入ってから選択肢の並びを変えると集計がずれるので、変えるときは別の id で出し直す。

export type SurveyQuestion =
  | { id: string; kind: 'single'; text: string; options: string[]; optional?: boolean }
  | { id: string; kind: 'multi'; text: string; options: string[]; max?: number; optional?: boolean }
  | { id: string; kind: 'text'; text: string; max?: number; placeholder?: string; optional?: boolean };

/** reward は worker の Grant[]（{ kind, amount } / { kind: 'item', id, amount }）。検証は worker の parseGrants */
export type SurveyDef = { id: string; title: string; questions: SurveyQuestion[]; reward: { kind: string; id?: string; amount: number }[] };

/** single → 番号、multi → 番号の配列、text → 文字列（空なら入れない） */
export type SurveyAnswers = Record<string, number | number[] | string>;

export const SURVEY_ID = /^[a-z0-9][a-z0-9-]{0,39}$/;
const QID = /^[a-zA-Z][a-zA-Z0-9_]{0,23}$/;
export const SURVEY_LIMITS = { title: 40, questions: 10, text: 60, options: 8, option: 20, textMax: 500 } as const;
const TEXT_DEFAULT_MAX = 200;

/** 1問の選べる上限（multi の max が無ければ全部） */
export const multiMax = (q: Extract<SurveyQuestion, { kind: 'multi' }>) => Math.min(q.max ?? q.options.length, q.options.length);
export const textMax = (q: Extract<SurveyQuestion, { kind: 'text' }>) => q.max ?? TEXT_DEFAULT_MAX;

const isStr = (v: unknown, max: number): v is string => typeof v === 'string' && v.trim().length > 0 && v.length <= max;

/** 運営APIから来た設問の定義を検証する。reward は形だけ見る（中身は worker が parseGrants で確かめる）。不正ならエラーの文字列 */
export function parseSurveyDef(input: unknown): SurveyDef | string {
  if (!input || typeof input !== 'object') return 'body';
  const b = input as Record<string, unknown>;
  if (typeof b.id !== 'string' || !SURVEY_ID.test(b.id)) return 'id';
  if (!isStr(b.title, SURVEY_LIMITS.title)) return 'title';
  if (!Array.isArray(b.questions) || !b.questions.length || b.questions.length > SURVEY_LIMITS.questions) return 'questions';
  if (!Array.isArray(b.reward)) return 'reward';
  const seen = new Set<string>();
  const questions: SurveyQuestion[] = [];
  for (const [i, raw] of b.questions.entries()) {
    const at = `questions.${i}`;
    if (!raw || typeof raw !== 'object') return at;
    const q = raw as Record<string, unknown>;
    if (typeof q.id !== 'string' || !QID.test(q.id) || seen.has(q.id)) return `${at}.id`;
    seen.add(q.id);
    if (!isStr(q.text, SURVEY_LIMITS.text)) return `${at}.text`;
    const optional = q.optional === true ? { optional: true } : {};
    if (q.kind === 'single' || q.kind === 'multi') {
      const opts = q.options;
      if (!Array.isArray(opts) || opts.length < 2 || opts.length > SURVEY_LIMITS.options || !opts.every((o) => isStr(o, SURVEY_LIMITS.option)) || new Set(opts).size !== opts.length)
        return `${at}.options`;
      if (q.kind === 'single') questions.push({ id: q.id, kind: 'single', text: q.text, options: opts as string[], ...optional });
      else {
        if (q.max !== undefined && (!Number.isSafeInteger(q.max) || (q.max as number) < 1 || (q.max as number) > opts.length)) return `${at}.max`;
        questions.push({ id: q.id, kind: 'multi', text: q.text, options: opts as string[], ...(q.max !== undefined ? { max: q.max as number } : {}), ...optional });
      }
    } else if (q.kind === 'text') {
      if (q.max !== undefined && (!Number.isSafeInteger(q.max) || (q.max as number) < 1 || (q.max as number) > SURVEY_LIMITS.textMax)) return `${at}.max`;
      if (q.placeholder !== undefined && (typeof q.placeholder !== 'string' || q.placeholder.length > SURVEY_LIMITS.text)) return `${at}.placeholder`;
      questions.push({
        id: q.id,
        kind: 'text',
        text: q.text,
        ...(q.max !== undefined ? { max: q.max as number } : {}),
        ...(q.placeholder ? { placeholder: q.placeholder as string } : {}),
        ...optional,
      });
    } else return `${at}.kind`;
  }
  return { id: b.id, title: (b.title as string).trim(), questions, reward: b.reward as SurveyDef['reward'] };
}

/** 回答を設問に照らして検証する。知らない設問は捨てる。不正なら { error: 設問ID } */
export function parseSurveyAnswers(def: SurveyDef, input: unknown): SurveyAnswers | { error: string } {
  const a = input && typeof input === 'object' && !Array.isArray(input) ? (input as Record<string, unknown>) : {};
  const out: SurveyAnswers = {};
  for (const q of def.questions) {
    const v = a[q.id];
    const empty = v === undefined || v === null || (Array.isArray(v) && !v.length) || (typeof v === 'string' && !v.trim());
    if (empty) {
      if (!q.optional) return { error: q.id };
      continue;
    }
    if (q.kind === 'single') {
      if (!Number.isSafeInteger(v) || (v as number) < 0 || (v as number) >= q.options.length) return { error: q.id };
      out[q.id] = v as number;
    } else if (q.kind === 'multi') {
      if (!Array.isArray(v) || v.length > multiMax(q) || new Set(v).size !== v.length || !v.every((x) => Number.isSafeInteger(x) && x >= 0 && x < q.options.length))
        return { error: q.id };
      out[q.id] = [...(v as number[])].sort((x, y) => x - y);
    } else {
      if (typeof v !== 'string') return { error: q.id };
      const t = v.trim();
      if (t.length > textMax(q)) return { error: q.id };
      out[q.id] = t;
    }
  }
  return out;
}

export type SurveyTally =
  | { id: string; kind: 'single' | 'multi'; text: string; answered: number; options: { label: string; n: number }[] }
  | { id: string; kind: 'text'; text: string; answered: number; texts: { text: string; by: string }[] };

/** 回答の集計（選択肢ごとの人数・自由記述の一覧）。by は運営が見る回答者の名前など */
export function tallySurvey(def: SurveyDef, rows: { answers: SurveyAnswers; by?: string }[]): SurveyTally[] {
  return def.questions.map((q) => {
    if (q.kind === 'text') {
      const texts = rows.flatMap((r) => (typeof r.answers[q.id] === 'string' ? [{ text: r.answers[q.id] as string, by: r.by ?? '' }] : []));
      return { id: q.id, kind: 'text', text: q.text, answered: texts.length, texts };
    }
    const n = q.options.map(() => 0);
    let answered = 0;
    for (const r of rows) {
      const v = r.answers[q.id];
      const picks = typeof v === 'number' ? [v] : Array.isArray(v) ? v : [];
      if (picks.length) answered++;
      for (const i of picks) if (i >= 0 && i < n.length) n[i]++;
    }
    return { id: q.id, kind: q.kind, text: q.text, answered, options: q.options.map((label, i) => ({ label, n: n[i] })) };
  });
}
